import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../services/authService';

const inputClass =
  'w-full px-5 py-4 rounded-xl bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition text-base';

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Step 1: Send OTP to email
  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.forgotPassword(email);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authService.verifyOtp(email, otp);
      setResetToken(response.resetToken || response.data?.resetToken);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(resetToken, password, confirmPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/auth/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendCode = async () => {
    setLoading(true);
    setError('');

    try {
      await authService.forgotPassword(email);
      setOtp('');
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  const labelClass = 'block text-base font-semibold text-jp-ink mb-2';
  const submitClass =
    'w-full py-4 text-base font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-section active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed';

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8" aria-label={`Step ${step} of 3`}>
      {[1, 2, 3].map((s, i) => (
        <React.Fragment key={s}>
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
              step > s
                ? 'bg-jp-accent text-white'
                : step === s
                ? 'bg-jp-accent text-white ring-4 ring-jp-accent/25'
                : 'bg-jp-field border border-jp-line text-jp-muted'
            }`}
          >
            {step > s ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : s}
          </div>
          {i < 2 && (
            <div className={`w-12 h-0.5 ${step > s ? 'bg-jp-accent' : 'bg-jp-line'} transition-all`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const heading = success
    ? 'ALL SET'
    : step === 1 ? 'FORGOT PASSWORD' : step === 2 ? 'VERIFY CODE' : 'NEW PASSWORD';

  const subheading = success
    ? 'Your password has been reset.'
    : step === 1
    ? 'Enter your email address and we\'ll send you a verification code.'
    : step === 2
    ? `We've sent a 6-digit code to ${email}`
    : 'Enter your new password below.';

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Navy strip behind the see-through navbar so its white logo and links stay readable */}
      <div className="bg-jp-band h-16 sm:h-20" aria-hidden="true" />

      <section className="px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-20">
        <div className="max-w-xl mx-auto text-center mb-8">
          <p className="text-jp-accent dark:text-jp-gold text-sm font-bold uppercase tracking-widest mb-3">
            Reset Your Password
          </p>
          <h1 className="text-5xl sm:text-6xl font-anton leading-[1.1] tracking-tight text-jp-ink">
            {heading}
          </h1>
          <p className="mt-4 text-jp-muted text-sm sm:text-base">
            {subheading}
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-5 sm:p-8">
          <div className="rounded-xl p-6 sm:p-10 bg-jp-section border border-jp-outline/50">
            {success ? (
              <div className="text-center py-4">
                <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-xl font-bold text-jp-ink mb-2">Password reset successful!</p>
                <p className="text-jp-muted text-sm">Taking you to sign in...</p>
              </div>
            ) : (
              <>
                <StepIndicator />

                {error && (
                  <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-700 dark:text-red-300 text-sm mb-6">
                    {error}
                  </div>
                )}

                {/* Step 1: Email */}
                {step === 1 && (
                  <form onSubmit={handleSendCode} className="space-y-6">
                    <div>
                      <label htmlFor="forgot-email" className={labelClass}>Email</label>
                      <input
                        id="forgot-email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your.email@example.com"
                        required
                        className={inputClass}
                      />
                    </div>

                    <button type="submit" className={submitClass} disabled={loading || !email}>
                      {loading ? 'Sending...' : 'Send Code'}
                    </button>
                  </form>
                )}

                {/* Step 2: OTP Verification */}
                {step === 2 && (
                  <form onSubmit={handleVerifyOtp} className="space-y-6">
                    <div>
                      <div className="flex items-baseline justify-between mb-2">
                        <label htmlFor="forgot-otp" className="block text-base font-semibold text-jp-ink">Verification code</label>
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={loading}
                          className="text-sm font-semibold text-jp-accent hover:underline focus:outline-none focus-visible:underline disabled:opacity-50"
                        >
                          Resend code
                        </button>
                      </div>
                      <input
                        id="forgot-otp"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={otp}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtp(val);
                        }}
                        placeholder="Enter 6-digit code"
                        required
                        maxLength={6}
                        className={`${inputClass} text-center text-2xl tracking-[0.5em] font-mono placeholder:text-base placeholder:tracking-normal placeholder:font-sans`}
                      />
                    </div>

                    <button type="submit" className={submitClass} disabled={loading || otp.length < 6}>
                      {loading ? 'Verifying...' : 'Verify'}
                    </button>
                  </form>
                )}

                {/* Step 3: New Password */}
                {step === 3 && (
                  <form onSubmit={handleResetPassword} className="space-y-6">
                    <div>
                      <label htmlFor="forgot-password" className={labelClass}>New password</label>
                      <input
                        id="forgot-password"
                        type="password"
                        name="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="8 or more characters"
                        required
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label htmlFor="forgot-confirm-password" className={labelClass}>Confirm password</label>
                      <input
                        id="forgot-confirm-password"
                        type="password"
                        name="confirmPassword"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Type it again"
                        required
                        className={inputClass}
                      />
                      {password && confirmPassword && (
                        <p className={`mt-2 text-sm ${password === confirmPassword ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                          {password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      className={submitClass}
                      disabled={loading || !password || !confirmPassword || password !== confirmPassword}
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>

          <p className="px-2 pt-5 pb-1 text-center text-sm sm:text-base text-jp-muted">
            Remembered it?{' '}
            <Link to="/auth/login" className="font-semibold text-jp-accent hover:underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
};

export default ForgotPasswordPage;
